import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as API from "../api/index";
import * as Yup from "yup";
import { NotificationManager } from "react-notifications";
import { TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useDataView } from "../../contexts/data.view.context";
import ConfirmAddDialog from "../util/confirmAddDialog";
import AddFormLayout from "../shared/AddFormLayout";
import SectionGrid from "../shared/SectionGrid";
import { useSectionedForm } from "../shared/useSectionedForm";

const FORM_SECTIONS = [
  {
    id: "study",
    label: "Study info",
    fields: ["PROJECT_ID", "PROTOCOL_ID", "STUDY_ID", "SPONSOR"],
  },
  {
    id: "patient",
    label: "Patient info",
    fields: ["SITE_ID", "PATIENT_ID", "STATE"],
  },
  {
    id: "consent",
    label: "Consent & notes",
    fields: ["TOKEN", "INFORMED_CONSENT_DATE", "COMMENTS"],
  },
];

const OuraPatientForm = () => {
  const validationSchema = Yup.object().shape({
    PROJECT_ID: Yup.string().required("Project ID is required"),
    SITE_ID: Yup.string().required("Site ID is required"),
    PATIENT_ID: Yup.string().required("Patient ID is required"),
    INFORMED_CONSENT_DATE: Yup.string().required(
      "Informed Consent date is required"
    ),
    STATE: Yup.string().required("State is required"),
    AGE: Yup.string().nullable().notRequired().default(""),
    WEIGHT: Yup.string().nullable().notRequired().default(""),
    HEIGHT: Yup.string().nullable().notRequired().default(""),
    GENDER: Yup.string().nullable().notRequired().default(""),
    PROTOCOL_ID: Yup.string().required("Protocol ID is required"),
    STUDY_ID: Yup.string().required("Connection Data is required"),
    SPONSOR: Yup.string().required("Auth Type is required"),
    TOKEN: Yup.string().required("Auth ID is required"),
    COMMENTS: Yup.string().required("Auth PIN is required"),
  });

  const {
    register,
    handleSubmit,
    trigger,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { sections, activeId, setActiveId, isFirst, isLast, goNext, goPrev } =
    useSectionedForm(FORM_SECTIONS, errors);

  const navigate = useNavigate();
  const { fetchOuraPatients } = useDataView();

  const stateItems = ["Active", "Disabled"];
  const [pendingPayload, setPendingPayload] = useState(null);

  const onSubmit = (data) => {
    data.INFORMED_CONSENT_DATE = dayjs(data.INFORMED_CONSENT_DATE).format(
      "YYYY-MM-DD"
    );

    setPendingPayload(data);
  };

  const handleConfirmAdd = () => {
    const data = pendingPayload;
    setPendingPayload(null);
    const resourcePath = "incyte/oura/onboard";
    API.AddOuraPatient(resourcePath, data).then((response) => {
      if (response.code === 200) {
        NotificationManager.success("Record added!", "Oura patient", 10000);
        fetchOuraPatients();
        navigate("/OuraPatients");
      } else {
        NotificationManager.error(response.message, "Failed!", 10000);
      }
    });
  };

  const handleNext = async () => {
    const current = sections[sections.findIndex((s) => s.id === activeId)];
    const valid = await trigger(current.fields);
    if (valid) goNext();
  };

  return (
    <>
      <AddFormLayout
        title="Add Oura patient"
        sections={sections}
        activeSectionId={activeId}
        onSectionChange={setActiveId}
        onBack={() => navigate(-1)}
        secondaryLabel={isFirst ? undefined : "Previous"}
        onSecondaryClick={goPrev}
        primaryLabel={isLast ? "Add" : "Next section"}
        onPrimaryClick={isLast ? handleSubmit(onSubmit) : handleNext}
      >
        {activeId === "study" && (
          <SectionGrid>
            <TextField
              required
              label="PROJECT_ID"
              size="small"
              fullWidth
              {...register("PROJECT_ID")}
              error={!!errors.PROJECT_ID}
              variant="outlined"
              defaultValue="INCY216559"
            />
            <TextField
              required
              label="PROTOCOL_ID"
              size="small"
              fullWidth
              {...register("PROTOCOL_ID")}
              error={!!errors.PROTOCOL_ID}
              variant="outlined"
              defaultValue="INCB 18424-902"
            />
            <TextField
              required
              label="STUDY_ID"
              size="small"
              fullWidth
              {...register("STUDY_ID")}
              error={!!errors.STUDY_ID}
              variant="outlined"
              defaultValue="Incyte_Ph4_AD_Sleep_Study"
            />
            <TextField
              required
              label="SPONSOR"
              size="small"
              fullWidth
              {...register("SPONSOR")}
              error={!!errors.SPONSOR}
              variant="outlined"
              defaultValue="INCYTE"
            />
          </SectionGrid>
        )}

        {activeId === "patient" && (
          <SectionGrid>
            <TextField
              required
              label="SITE_ID"
              size="small"
              fullWidth
              {...register("SITE_ID")}
              error={!!errors.SITE_ID}
              variant="outlined"
            />
            <TextField
              required
              label="PATIENT_ID"
              size="small"
              fullWidth
              {...register("PATIENT_ID")}
              error={!!errors.PATIENT_ID}
              variant="outlined"
            />
            <TextField
              select
              label="Patient Status"
              size="small"
              fullWidth
              {...register("STATE")}
              error={!!errors.STATE}
              variant="outlined"
            >
              {stateItems.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </SectionGrid>
        )}

        {activeId === "consent" && (
          <SectionGrid>
            <TextField
              required
              label="TOKEN"
              size="small"
              fullWidth
              {...register("TOKEN")}
              error={!!errors.TOKEN}
              variant="outlined"
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                control={control}
                name="INFORMED_CONSENT_DATE"
                render={({
                  field: {
                    ref,
                    onBlur,
                    name,
                    margin,
                    size,
                    variant,
                    ...field
                  },
                  fieldState,
                }) => (
                  <DatePicker
                    {...field}
                    inputRef={ref}
                    label="INFORMED_CONSENT_DATE"
                    slotProps={{
                      textField: {
                        size: "small",
                        name: "INFORMED_CONSENT_DATE",
                        margin: "dense",
                        variant: "outlined",
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <SectionGrid.FullWidth>
              <TextField
                required
                label="COMMENTS"
                size="small"
                fullWidth
                {...register("COMMENTS")}
                error={!!errors.COMMENTS}
                variant="outlined"
              />
            </SectionGrid.FullWidth>
          </SectionGrid>
        )}
      </AddFormLayout>
      <ConfirmAddDialog
        open={!!pendingPayload}
        onClose={() => setPendingPayload(null)}
        onConfirm={handleConfirmAdd}
        itemLabel="Oura patient"
      />
    </>
  );
};

export default OuraPatientForm;
